<template>
    <vx-expandable-card>
        <template v-slot:header>Group Status Default</template>
        <template v-slot:headerSubtitle> What status should new groups default to when they are created? </template>
        <template v-if="loading" v-slot:content>
            <page-loading class="py-8"></page-loading>
        </template>
        <template v-else v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <vx-radio-selection
                    v-model="defaultGroupStatus"
                    :disabled="!props.isEditable"
                    aria-label="Default group status"
                >
                    <vx-radio-selection-item value="draft" data-test="default-group-status-draft">
                        <template v-slot:header>Draft</template>
                        <template v-slot:content>
                            Groups are created in a draft state and must be manually activated.
                        </template>
                    </vx-radio-selection-item>
                    <vx-radio-selection-item value="active" data-test="default-group-status-active">
                        <template v-slot:header>Active</template>
                        <template v-slot:content>
                            Groups are created in an active state and are immediately operational.
                        </template>
                    </vx-radio-selection-item>
                </vx-radio-selection>

                <vx-button
                    v-if="props.isEditable"
                    size="lg"
                    @click.prevent="submit"
                    :loading="submitting"
                    data-test="submit-button"
                >
                    Update
                </vx-button>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxExpandableCard, VxRadioSelection, VxRadioSelectionItem, VxButton } from '@voxie/frontend-components';
import PageLoading from '~/components/general/PageLoading.vue';
import { onMounted, ref } from 'vue';
import { adminTeamsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';

const props = withDefaults(
    defineProps<{
        teamId: number;
        isEditable?: boolean;
    }>(),
    {
        isEditable: true,
    }
);

const toasted = useToasted();
const { catchError } = useFormRequestHandler();
const loading = ref(true);
const submitting = ref(false);
const defaultGroupStatus = ref<'draft' | 'active'>('draft');

onMounted(async () => {
    try {
        const response = await adminTeamsClient.getDefaultGroupStatus(props.teamId);
        defaultGroupStatus.value = response.data.default_group_status;
    } catch (e) {
        console.error(e);
        catchError(e);
    } finally {
        loading.value = false;
    }
});

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        await adminTeamsClient.updateDefaultGroupStatus(props.teamId, {
            default_group_status: defaultGroupStatus.value,
        });

        toasted.success('Group status default updated successfully');
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
